import { useToasts } from '@brunommsilva/react-foundations';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TEST_IDS } from '../../config/test-ids';
import useUpdateAccount from '../../hooks/accounts/use-update-account';
import { Account, AccountType } from '../../models/accounts';
import AccountTypeDropdown from './account-type-dropdown';

interface State {
  name: string;
  type?: AccountType;
}

interface Props {
  show: boolean;
  account?: Account;
  onEdited: () => void;
  onClose: () => void;
}

function EditAccountModal({ show, account, onEdited, onClose }: Props) {
  const { t } = useTranslation();
  const { showToast } = useToasts();
  const [state, setState] = useState<State>({ name: '' });
  const {
    updateAccount,
    isUpdating,
    wasUpdatedWithSuccess,
    wasUpdatedWithoutSuccess,
  } = useUpdateAccount();

  useEffect(() => {
    if (account) {
      setState({ name: account.name, type: account.type });
    }
  }, [account]);

  useEffect(() => {
    if (wasUpdatedWithSuccess) {
      showToast({
        title: t('EDIT_ACCOUNT_SUCCESS_TOAST.TITLE'),
        message: t('EDIT_ACCOUNT_SUCCESS_TOAST.MESSAGE'),
      });
      onEdited();
    }
    if (wasUpdatedWithoutSuccess) {
      showToast({
        title: t('EDIT_ACCOUNT_ERROR_TOAST.TITLE'),
        message: t('EDIT_ACCOUNT_ERROR_TOAST.MESSAGE'),
        type: 'error',
      });
    }
  }, [wasUpdatedWithSuccess, wasUpdatedWithoutSuccess]);

  const isFormValid = () => {
    return account && state.name.trim() && state.type;
  };

  const handleEditAccount = () => {
    if (!isFormValid()) {
      return;
    }

    updateAccount({
      id: account!.id,
      name: state.name.trim(),
      type: state.type!,
    });
  };

  return (
    <Dialog open={show} onClose={onClose} fullWidth>
      <DialogTitle>{t('EDIT_ACCOUNT_MODAL.TITLE')}</DialogTitle>
      <DialogContent dividers>
        <Stack sx={{ gap: 2 }}>
          <FormControl fullWidth>
            <TextField
              label={t('ACCOUNT_NAME')}
              value={state.name}
              onChange={(change: React.ChangeEvent<HTMLInputElement>) => {
                setState({ ...state, name: change.target.value });
              }}
              slotProps={{
                htmlInput: { 'data-testid': TEST_IDS.accounts.nameInput },
              }}
            />
          </FormControl>
          <AccountTypeDropdown
            selected={
              state.type
                ? { id: state.type, name: t(`ACCOUNT_TYPE_${state.type}`) }
                : undefined
            }
            onSelected={(type) =>
              setState({ ...state, type: type?.id as AccountType })
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isUpdating} color="primary" onClick={onClose}>
          {t('EDIT_ACCOUNT_MODAL.CLOSE')}
        </Button>
        <Button
          disabled={isUpdating || !isFormValid()}
          color="error"
          onClick={handleEditAccount}
        >
          {t('EDIT_ACCOUNT_MODAL.CONFIRM')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditAccountModal;
